/**
 * useRankingWeights Hook
 * 
 * Manages ranking weights for search results.
 * Persists to localStorage and syncs to backend when authenticated.
 */

import { useState, useCallback } from 'react';
import { DEFAULT_WEIGHTS, mergeWithDefaults } from '../config/ranking-weights';

const STORAGE_KEY = 'eq_ranking_weights';

/**
 * Load weights from localStorage
 */
function loadStoredWeights() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return mergeWithDefaults(parsed);
    }
  } catch (e) {
    console.warn('Failed to load stored weights:', e);
  }
  return DEFAULT_WEIGHTS;
}

/**
 * Save weights to localStorage
 */
function saveStoredWeights(weights) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(weights));
  } catch (e) {
    console.warn('Failed to save weights:', e);
  }
}

/**
 * Hook for managing ranking weights
 * 
 * @returns {Object} { weights, setWeights, resetWeights }
 */
export function useRankingWeights() {
  const [weights, setWeightsState] = useState(loadStoredWeights);
  
  // Save to localStorage whenever weights change
  const setWeights = useCallback((newWeights) => {
    const merged = mergeWithDefaults(newWeights);
    setWeightsState(merged);
    saveStoredWeights(merged);
  }, []);
  
  // Reset to defaults
  const resetWeights = useCallback(() => {
    setWeightsState(DEFAULT_WEIGHTS);
    saveStoredWeights(DEFAULT_WEIGHTS);
  }, []);
  
  return {
    weights,
    setWeights,
    resetWeights,
  };
}

/**
 * Get current weights from localStorage (for use in search calls)
 */
export function getCurrentWeights() {
  return loadStoredWeights();
}

export default useRankingWeights;



